//frontend/src/components/admin/AdminDeliveredOrders.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
    Package, 
    MapPin, 
    User, 
    Clock, 
    Eye, 
    RefreshCw, 
    Search,
    Download,
    Calendar,
    ArrowLeft
} from 'lucide-react';
import axios from 'axios';

const AdminDeliveredOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [downloadLoading, setDownloadLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchDeliveredOrders();
    }, []);

    const fetchDeliveredOrders = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/delivered-orders', {
                withCredentials: true
            });
            if (data.success) {
                setOrders(data.orders);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching delivered orders');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadReport = async () => {
        if (!startDate || !endDate) {
            toast.error('Please select both start and end dates');
            return;
        }

        try {
            setDownloadLoading(true);
            const response = await axios.get(
                `/api/v1/admin/delivered-orders/report?startDate=${startDate}&endDate=${endDate}`,
                {
                    responseType: 'blob',
                    withCredentials: true
                }
            );

            // Create a download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `delivered-orders-${startDate}-to-${endDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Report downloaded successfully');
        } catch (error) {
            toast.error('Error downloading report');
            console.error('Download error:', error);
        } finally {
            setDownloadLoading(false);
        }
    };

    const getAreaName = (pincode) => {
        const areas = {
            '562130': 'Gollarahatti',
            '562130': 'Kadabagere',
            '560112': 'Chikka Gollarahatti',
            '560091': 'Machohalli',
            '560091': 'Anjana Nagar',
            '560091': 'Byadarahalli',
            '562130': 'Tavarekere',
            '560091': 'Herohalli',
            '560091': 'Viswaneedam',
            '560091': 'Sunkadakatte',
            '560091': 'Muddayanapalya',
            '560091': 'Annapurneshwari Nagar',
            '560091': 'Kottigepalya',
            '560091': 'Sumanahalli'
        };
        return areas[pincode] || pincode;
    };

    const filteredOrders = orders.filter(order =>
        order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getAreaName(order.shippingInfo?.postalCode || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.deliveryStaffName || order.deliveryStaff?.name || 'Unassigned').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const OrderCard = ({ order }) => (
        <div className="bg-white rounded-lg shadow-sm p-3 mb-3">
            <div className="flex justify-between items-start mb-2">
                <div>
                    <p className="text-xs font-semibold text-gray-900">Order #{order.orderId}</p>
                    <p className="text-base font-bold text-[#dc2626] mt-1">₹{order.totalPrice}</p>
                </div>
                <button
                    onClick={() => navigate(`/admin/delivered-order/${order._id}`)}
                    className="p-1.5 text-[#dc2626] hover:bg-red-50 rounded-full transition-colors"
                >
                    <Eye className="h-4 w-4" />
                </button>
            </div>
            
            <div className="space-y-1.5">
                <div className="flex items-center text-xs">
                    <MapPin className="w-3 h-3 text-gray-400 mr-1.5" />
                    <span className="text-gray-600">{getAreaName(order.shippingInfo.postalCode)}</span>
                </div>
                
                <div className="flex items-center text-xs">
                    <User className="w-3 h-3 text-gray-400 mr-1.5" />
                    <span className="text-gray-600">{order.deliveryStaffName}</span>
                </div>
                
                <div className="flex items-center text-xs">
                    <Clock className="w-3 h-3 text-gray-400 mr-1.5" />
                    <div>
                        <span className="text-gray-600">{order.deliveryTime}</span>
                        <span className="text-gray-400 text-xxs block">
                            {new Date(order.deliveredAt).toLocaleDateString('en-IN')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <RefreshCw className="w-6 h-6 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    return (
        <div className="container mx-auto px-3 py-6">
            {/* Back to Dashboard Button */}
            <button
                onClick={() => navigate('/admin/dashboard')}
                className="mb-4 flex items-center gap-1.5 text-gray-600 hover:text-[#dc2626] transition-colors text-sm"
            >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Dashboard</span>
            </button>

            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 font-['Crimson_Text']">
                                Delivered Orders
                            </h2>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Total Delivered Orders: {orders.length}
                            </p>
                        </div>

                        {/* Date Range Selector and Download Section */}
                        <div className="flex flex-col md:flex-row gap-3">
                            <div className="flex items-center gap-1.5 text-sm">
                                <Calendar className="w-3 h-3 text-gray-400" />
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
                                />
                                <span className="text-xs">to</span>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="border border-gray-300 rounded-lg px-2 py-1 text-xs"
                                />
                                <button
                                    onClick={handleDownloadReport}
                                    disabled={downloadLoading || !startDate || !endDate}
                                    className="flex items-center gap-1.5 px-3 py-1 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                >
                                    {downloadLoading ? (
                                        <RefreshCw className="w-3 h-3 animate-spin" />
                                    ) : (
                                        <Download className="w-3 h-3" />
                                    )}
                                    {downloadLoading ? 'Downloading...' : 'Download Report'}
                                </button>
                            </div>
                            
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search orders..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full md:w-56 pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#dc2626] focus:border-transparent text-xs"
                                />
                                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Desktop View */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-xxs font-medium text-gray-500 uppercase">
                                    Order ID
                                </th>
                                <th className="px-4 py-2 text-left text-xxs font-medium text-gray-500 uppercase">
                                    Delivery Area
                                </th>
                                <th className="px-4 py-2 text-left text-xxs font-medium text-gray-500 uppercase">
                                    Delivery Staff
                                </th>
                                <th className="px-4 py-2 text-left text-xxs font-medium text-gray-500 uppercase">
                                    Delivered At
                                </th>
                                <th className="px-4 py-2 text-left text-xxs font-medium text-gray-500 uppercase">
                                    Amount
                                </th>
                                <th className="px-4 py-2 text-left text-xxs font-medium text-gray-500 uppercase">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredOrders.map((order) => (
                                <tr key={order._id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <span className="text-xs font-medium text-gray-900">
                                            {order.orderId}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <MapPin className="w-3 h-3 text-gray-400 mr-1.5" />
                                            <div>
                                                <p className="text-xs font-medium text-gray-900">
                                                    {getAreaName(order.shippingInfo.postalCode)}
                                                </p>
                                                <p className="text-xxs text-gray-500">
                                                    {order.shippingInfo.postalCode}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <User className="w-3 h-3 text-gray-400 mr-1.5" />
                                            <span className="text-xs text-gray-900">
                                                {order.deliveryStaffName}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <Clock className="w-3 h-3 text-gray-400 mr-1.5" />
                                            <div>
                                                <p className="text-xs text-gray-900">
                                                    {order.deliveryTime}
                                                </p>
                                                <p className="text-xxs text-gray-500">
                                                    {new Date(order.deliveredAt).toLocaleDateString('en-IN')}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <span className="text-xs font-medium text-[#dc2626]">
                                            ₹{order.totalPrice}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <button
                                            onClick={() => navigate(`/admin/delivered-order/${order._id}`)}
                                            className="text-[#dc2626] hover:bg-red-50 p-1.5 rounded-full transition-colors"
                                        >
                                            <Eye className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View */}
                <div className="md:hidden">
                    <div className="space-y-3 p-3">
                        {filteredOrders.map((order) => (
                            <OrderCard key={order._id} order={order} />
                        ))}
                    </div>
                </div>

                {filteredOrders.length === 0 && (
                    <div className="text-center py-8">
                        <Package className="mx-auto h-10 w-10 text-gray-400" />
                        <h3 className="mt-2 text-xs font-medium text-gray-900">No Delivered Orders</h3>
                        <p className="mt-1 text-xs text-gray-500">
                            {searchTerm ? 'No orders match your search criteria.' : 'No orders have been delivered yet.'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDeliveredOrders;